import React, { useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Polygon,
  InfoWindow
} from '@react-google-maps/api';
import { useCurrentUser } from '../../_services/hooks';
import { servicesAPI } from '../../_services';
import { Hub } from '../../_components/map/Features/Hub';
import { ChargePoint } from '../../_components/map/Features/ChargePoint';
import { DropPin } from '../../_components/map/Features/DropPin';
import { UnderConstruction } from '../../_components/map/Features/UnderConstruction';
import { barnetAreaLatLongs } from '../../data';

const Config = require('../../conf');

const NO_UID = '00000000-0000-0000-0000-000000000000';
const googleMapsApiKey = 'AIzaSyC1_si0euik2ZbMw1dCcJHwqSEVGkDdcbQ'; // TDTODO move to Secret Manager
// const googleMapsMapId = '546899008cf3b525';

const libraries = ['places'];

const makeFeaturesFromHUB = (hubLocation, chargeStations, lod) => {
  const description = hubLocation.description;
  let latLonArray = hubLocation.lat_lons;

  const features = latLonArray.map((latLon, idx) => {
    let commissioning = false;
    let maintenance = false;
    let capped = false;
    let muted = false;
    let faulted = false;
    let charging = false;
    let future = hubLocation.lat_lons.length === 0;
    let cs = {
      hydraReference: hubLocation.reference,
      description: hubLocation.description
    };
    const csId = String(idx + 1).padStart(2, '0');
    const url =
      Config.Local().portal_url +
      `/admin/hubs/${hubLocation.reference}/cs_${csId}`;

    if (chargeStations.length) {
      cs = {};
      if (idx < chargeStations.length) cs = chargeStations[idx];

      cs.hydraReference = hubLocation.reference;
      cs.description = hubLocation.description;

      commissioning = cs.tag_status === 'COMMISSIONING';
      maintenance = cs.tag_status === 'MAINTENANCE';
      capped = cs.status === 'Capped';
      muted = cs.status === 'Stopped';
      faulted = cs.status === 'Faulted';
      charging =
        cs.status === 'Occupied' &&
        cs.connected_lance_tag_id !== NO_UID &&
        cs.connected_lance_tag_id &&
        cs.transaction_id !== NO_UID;
    }

    return {
      isHUB: false,
      cs: cs,
      id: csId,
      latLon: latLon,
      url: url,
      future: future,
      commissioning: commissioning,
      maintenance: maintenance,
      muted: muted,
      capped: capped,
      faulted: faulted,
      charging: charging,
      description: `${description} ${
        future ? '' : ' CS' + String(idx + 1).padStart(2, '0')
      }`,
      type: 'HUB',
      status: future
        ? 'Under Construction'
        : commissioning
        ? 'Commissioning'
        : maintenance
        ? 'Maintenance'
        : capped
        ? 'Capped'
        : muted
        ? 'Muted'
        : faulted
        ? 'Faulted'
        : charging
        ? 'Charging'
        : 'Available',
      link: url,
      hide_phalanx: cs
    };
  });

  if (lod === 'HIGH') return features;

  let numCSs = features.length;
  let latLon = features.length
    ? features[Math.floor(features.length / 2)].latLon
    : null;

  let numCharging = 0;
  let numFaulted = 0;
  let future = false;

  features.forEach(f => {
    if (f.capped) return;
    if (f.charging) numCharging++;
    if (f.faulted) numFaulted++;
    if (f.future) future = true;
  });

  const url =
    Config.Local().portal_url + '/admin/hubs/' + hubLocation.reference;

  return [
    {
      isHUB: true,
      id: `${numCSs}`,
      latLon: latLon,
      url: url,
      numCSs: numCSs,
      future: future,
      commissioning: false,
      maintenance: false,
      muted: false,
      capped: false,
      faulted: numFaulted,
      charging: numCharging,
      description: hubLocation.description,
      type: 'HUB',
      status: '',
      link: url,
      hide_phalanx: false
    }
  ];
};

export const Map = ({
  center,
  zoom,
  setZoom,
  dropPinLongLat,
  showHydras,
  showAons,
  showUnderConstruction,
  showCustomerView,
  showControlledParkingZones,
  setPhalanx,
  showCircle,
  filterHydraRef,
  circleCenter,
  exportParkingData,
  nightMode
}) => {
  // const [dbgZoom, setDbgZoom] = useState(18.85);
  // const [dbgX, setDbgX] = useState(-0.1636626128);
  // const [dbgY, setDbgY] = useState(51.55617692);

  const { currentUser } = useCurrentUser();

  const [hubLocationInfo, setHubLocationInfo] = useState(
    window.hubLocationInfo
  );
  const [loadingHubInfo, setLoadingHubInfo] = useState(true);

  const [map, setMap] = useState(null);

  const [phalanxes, setPhalanxes] = useState(null);
  // const [customers, setCustomers] = useState([]);

  const [parkingZones, setParkingZones] = useState([]);
  const [occupiedSpaces, setOccupiedSpaces] = useState([]);
  const [freeSpaces, setFreeSpaces] = useState([]);

  const [selectedMarker, setSelectedMarker] = useState(null);
  const [hydraFeatures, setHydraFeatures] = useState([]);
  const [lod, setLOD] = useState('HIGH');

  // Load Google Maps API
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries
  });

  async function getParkingData() {
    try {
      const zones = await servicesAPI.AdminServices('getParkingZones');
      zones.forEach(zone => {
        zone.data = JSON.parse(zone.data);
      });

      setParkingZones(zones);

      let occupied = [];
      let free = [];
      let overlapping = [];

      zones.forEach(zone => {
        zone.data.spaces.forEach(space => {
          if (space.type === 'occupied') occupied.push(space);
          else if (space.type === 'free') free.push(space);
          else overlapping.push(space);
        });
      });

      setOccupiedSpaces(occupied);
      setFreeSpaces(free);
    } catch (error) {}
  }

  async function getHydraPhalanxes() {
    servicesAPI.InventoryServices('getAllPhalanxes').then(data => {
      // Parse data
      let allPhalanxes = [];
      const keys = Object.keys(data);
      for (const key of keys) {
        try {
          allPhalanxes.push(JSON.parse(data[key]));
        } catch (error) {}
      }

      // Match HUBs to CSs
      let phalanxes = {};

      for (let i = 0; i < hubLocationInfo.length; i++) {
        const hubLocation = hubLocationInfo[i];
        if (hubLocation.tag_id === 'MATG99999999-9999-9999-9999-999999999999')
          continue;

        if (filterHydraRef && hubLocation.reference !== filterHydraRef)
          continue;

        const hubPhalanxes = allPhalanxes.filter(
          p =>
            p.reference.startsWith(hubLocation.reference) &&
            !p.reference.endsWith('cs_00')
        );

        phalanxes[hubLocation.tag_id] = hubPhalanxes;
      }
      setPhalanxes(phalanxes);
    });
  }

  const exportParkingCSV = () => {
    let csvContent =
      'Name, Description, HUB Reference, ZoneALatitude, ZoneALongitude, ZoneBLatitude, ZoneBLongitude, ZoneCLatitude, ZoneCLatitude, ZoneDLatitude, ZoneDLongitude, SpaceType, SpaceALatitude, SpaceALongitude, SpaceBLatitude, SpaceBLongitude, SpaceCLatitude, SpaceCLatitude, SpaceDLatitude, SpaceDLongitude, ArrivalTime, DepartureTime\n';

    parkingZones.forEach(parkingZone => {
      const zone = parkingZone.data;
      zone.spaces.forEach(space => {
        csvContent += `${zone.name},${zone.description},${zone.hydra_reference},${zone.ax},${zone.ay},${zone.bx},${zone.by},${zone.cx},${zone.cy},${zone.dx},${zone.dy},${space.type},${space.raw_ax},${space.raw_ay},${space.raw_bx},${space.raw_by},${space.raw_cx},${space.raw_cy},${space.raw_dx},${space.raw_dy},${space.arrival},${space.departure}\n`;
      });
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' });
    const objUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `Parking data.csv`;
    link.href = objUrl;
    link.click();
  };

  useEffect(() => {
    if (!hubLocationInfo) return;

    getParkingData();

    getHydraPhalanxes();

    // servicesAPI.CustomerServices().then(customers => {
    //   servicesAPI.CustomerServices('getCustomerLatLons').then(latLons => {
    //     setCustomers(
    //       customers.map(customer => {
    //         const latLon = latLons.find(ll => ll.customer_id === customer.id);
    //         return {
    //           ...customer,
    //           latLon: latLon ? [latLon.lat, latLon.lng] : [0, 0]
    //         };
    //       })
    //     );
    //   });
    // });
  }, [hubLocationInfo]); // eslint-disable-line

  useEffect(() => {
    if (!currentUser) return;
  }, [currentUser]); // eslint-disable-line

  useEffect(() => {
    if (!hubLocationInfo && loadingHubInfo) {
      servicesAPI
        .InventoryServices('getPhalanxLocationInfo')
        .then(hubLocationInfo => {
          let newHubLocationInfo = [];
          for (const hydraRef of Object.keys(hubLocationInfo)) {
            newHubLocationInfo.push({
              ...hubLocationInfo[hydraRef],
              reference: hydraRef
            });
          }
          window.hubLocationInfo = newHubLocationInfo;

          setLoadingHubInfo(false);
          setHubLocationInfo(newHubLocationInfo);
        });
    }
  }, [hubLocationInfo, loadingHubInfo]);

  useEffect(() => {
    if (!hubLocationInfo || !phalanxes) return;

    let features = [];

    hubLocationInfo
      .filter(
        phalanxLocation =>
          showUnderConstruction || phalanxLocation.reference !== 'hub_l999901'
      )
      .filter(
        phalanxLocation =>
          !filterHydraRef || phalanxLocation.reference === filterHydraRef
      )
      .forEach(phalanxLocation => {
        let locPhalanxes = phalanxes[phalanxLocation.tag_id];
        if (!locPhalanxes) locPhalanxes = [];

        features = [
          ...features,
          ...makeFeaturesFromHUB(phalanxLocation, locPhalanxes, lod)
        ];
      });

    setHydraFeatures(features);
  }, [hubLocationInfo, phalanxes, showUnderConstruction, lod]); // eslint-disable-line

  useEffect(() => {
    if (!parkingZones.length || exportParkingData === null) return;
    exportParkingCSV();
  }, [parkingZones, exportParkingData]); // eslint-disable-line

  if (loadError) return <div>Error loading map</div>;

  if (!isLoaded) return <div>Loading map</div>;

  if (!hydraFeatures.length) return <div>Getting HUB data</div>;

  // Red car
  // const carMarker = {
  //   path:
  //     'M29.395,0H17.636c-3.117,0-5.643,3.467-5.643,6.584v34.804c0,3.116,2.526,5.644,5.643,5.644h11.759   c3.116,0,5.644-2.527,5.644-5.644V6.584C35.037,3.467,32.511,0,29.395,0z M34.05,14.188v11.665l-2.729,0.351v-4.806L34.05,14.188z    M32.618,10.773c-1.016,3.9-2.219,8.51-2.219,8.51H16.631l-2.222-8.51C14.41,10.773,23.293,7.755,32.618,10.773z M15.741,21.713   v4.492l-2.73-0.349V14.502L15.741,21.713z M13.011,37.938V27.579l2.73,0.343v8.196L13.011,37.938z M14.568,40.882l2.218-3.336   h13.771l2.219,3.336H14.568z M31.321,35.805v-7.872l2.729-0.355v10.048L31.321,35.805',
  //   fillColor: 'red',
  //   fillOpacity: 2,
  //   strokeWeight: 1,
  //   rotation: 0,
  //   scale: 1
  // };

  // Drop pin
  // const dropPinMarker = 'http://maps.google.com/mapfiles/ms/icons/pink-dot.png';

  const dropPinIcon = () => {
    return {
      anchor: new window.google.maps.Point(16, 16),
      url:
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(renderToStaticMarkup(<DropPin w={36} h={36} />))
    };
  };

  const underConstructionIcon = f => {
    return {
      anchor: new window.google.maps.Point(16, 16),
      url:
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(
          renderToStaticMarkup(<UnderConstruction w={36} h={36} />)
        )
    };
  };

  const chargePointIcon = f => {
    return {
      anchor: new window.google.maps.Point(16, 16),
      url:
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(
          renderToStaticMarkup(
            <ChargePoint
              w={36}
              h={36}
              colour={
                f.future
                  ? 'yellow'
                  : f.commissioning
                  ? 'orange'
                  : f.maintenance
                  ? 'orange'
                  : f.muted || f.capped
                  ? 'grey'
                  : f.faulted
                  ? 'red'
                  : f.charging
                  ? 'blue'
                  : 'green'
              }
              cpId={f.id}
              l1={f.charging}
              l2={f.charging}
              l3={f.charging}
              car={f.charging}
              lance={f.charging}
            />
          )
        )
    };
  };

  const hubIcon = f => {
    const txt = f.charging ? `${f.charging}` : '';
    // const txt1 = `${f.charging} / ${f.numCSs - (f.charging + f.faulted)}`;
    // const txt2 = `${f.faulted} / ${f.numCSs}`;
    return {
      anchor: new window.google.maps.Point(16, 16),
      url:
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(
          renderToStaticMarkup(
            <Hub
              w={48}
              h={48}
              colour={
                f.future
                  ? 'yellow'
                  : // : f.commissioning
                  // ? 'orange'
                  // : f.maintenance
                  // ? 'orange'
                  // : f.muted || f.capped
                  // ? 'grey'
                  // : f.faulted
                  // ? 'red'
                  f.charging
                  ? 'blue'
                  : 'green'
              }
              txt={txt}
            />
          )
        )
    };
  };

  const defaultStyles = [
    {
      featureType: 'poi.business',
      stylers: [{ visibility: 'off' }]
    }
  ];

  let nightModeStyles = [
    ...defaultStyles,
    { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
    {
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#242f3e' }]
    },
    {
      elementType: 'labels.text.fill',
      stylers: [{ color: '#746855' }]
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }]
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }]
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{ color: '#263c3f' }]
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#6b9a76' }]
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#38414e' }]
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#212a37' }]
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#9ca5b3' }]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{ color: '#746855' }]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#1f2835' }]
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#f3d19c' }]
    },
    {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [{ color: '#2f3948' }]
    },
    {
      featureType: 'transit.station',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#d59563' }]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#17263c' }]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#515c6d' }]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [{ color: '#17263c' }]
    }
  ];

  const onMapMounted = ref => {
    if (ref && !map) setMap(ref);
  };

  return (
    <>
      <GoogleMap
        options={{
          streetViewControl: false,
          gestureHandling: 'greedy',
          styles: nightMode ? nightModeStyles : defaultStyles
        }}
        mapContainerStyle={{
          width: '100%',
          height: '800px'
        }}
        zoom={zoom}
        center={{
          lat: center[1],
          lng: center[0]
        }}
        ref={onMapMounted}
        onZoomChanged={() => {
          if (map) {
            const zoom = map.state.map.zoom;
            setZoom(zoom);

            // 00-13 Low
            // 14-16 Medium
            // 17-99 High
            // const newLOD = zoom >= 17 ? 'HIGH' : zoom >= 14 ? 'MEDIUM' : 'LOW';
            const newLOD = zoom >= 17 ? 'HIGH' : 'MEDIUM';

            if (newLOD !== lod) setLOD(newLOD);
          }
        }}
        // mapId={googleMapsMapId}
      >
        {selectedMarker ? (
          <InfoWindow
            onCloseClick={() => {
              setSelectedMarker(null);
            }}
            position={{
              lat: selectedMarker.latLon[0],
              lng: selectedMarker.latLon[1]
            }}
          >
            <div>
              {selectedMarker.description ? (
                selectedMarker.link ? (
                  <>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={selectedMarker.link}
                    >
                      {selectedMarker.description}
                    </a>
                  </>
                ) : (
                  selectedMarker.description
                )
              ) : (
                ''
              )}
              <br />
              {selectedMarker.type ? 'Type: ' + selectedMarker.type : ''}
              <br />
              {selectedMarker.status ? 'Status: ' + selectedMarker.status : ''}
            </div>
          </InfoWindow>
        ) : (
          ''
        )}
        {showHydras &&
          hydraFeatures
            .filter(f => {
              return showCustomerView
                ? !f.future &&
                    !f.commissioning &&
                    !f.maintenance &&
                    !f.capped &&
                    !f.muted &&
                    !f.faulted &&
                    !f.charging
                : true;
            })
            .map(f => {
              return f.future ? (
                <Marker
                  position={{
                    lat: f.latLon[0],
                    lng: f.latLon[1]
                  }}
                  icon={underConstructionIcon(f)}
                  onClick={() => {
                    setSelectedMarker(f);
                  }}
                />
              ) : f.isHUB ? (
                <Marker
                  position={{
                    lat: f.latLon[0],
                    lng: f.latLon[1]
                  }}
                  icon={hubIcon(f)}
                  onClick={() => {
                    setSelectedMarker(f);
                  }}
                />
              ) : (
                <Marker
                  position={{
                    lat: f.latLon[0],
                    lng: f.latLon[1]
                  }}
                  icon={chargePointIcon(f)}
                  onClick={() => {
                    setSelectedMarker(f);
                  }}
                />
              );
            })}
        {showControlledParkingZones ? (
          <Polygon
            path={barnetAreaLatLongs.map(latLon => {
              return {
                lat: latLon[0],
                lng: latLon[1]
              };
            })}
            options={{
              fillColor: '#7777ff',
              fillOpacity: 0.2,
              strokeColor: '#8cc8f8',
              strokeOpacity: 0.8,
              strokeWeight: 3
            }}
            onClick={() => {}}
          />
        ) : (
          ''
        )}
        {!showCustomerView && lod === 'HIGH'
          ? parkingZones.map(z => (
              <Polygon
                path={[
                  { lat: z.data.ax, lng: z.data.ay },
                  { lat: z.data.bx, lng: z.data.by },
                  { lat: z.data.cx, lng: z.data.cy },
                  { lat: z.data.dx, lng: z.data.dy }
                ]}
                options={{
                  fillColor: 'white',
                  fillOpacity: 0.0,
                  strokeColor: '#0000ff',
                  strokeOpacity: 0.8,
                  strokeWeight: 3
                }}
                onClick={() => {}}
              />
            ))
          : ''}
        {!showCustomerView && lod === 'HIGH'
          ? occupiedSpaces.map(s => (
              <Polygon
                path={[
                  { lat: s.raw_ax, lng: s.raw_ay },
                  { lat: s.raw_bx, lng: s.raw_by },
                  { lat: s.raw_cx, lng: s.raw_cy },
                  { lat: s.raw_dx, lng: s.raw_dy }
                ]}
                options={{
                  fillColor: 'red',
                  fillOpacity: 0.4,
                  strokeColor: '#d35400',
                  strokeOpacity: 0.8,
                  strokeWeight: 3
                }}
                onClick={() => {}}
              />
            ))
          : ''}
        {lod === 'HIGH' &&
          freeSpaces.map(s => (
            <Polygon
              path={[
                { lat: s.raw_ax, lng: s.raw_ay },
                { lat: s.raw_bx, lng: s.raw_by },
                { lat: s.raw_cx, lng: s.raw_cy },
                { lat: s.raw_dx, lng: s.raw_dy }
              ]}
              options={{
                fillColor: 'green',
                fillOpacity: 0.4,
                strokeColor: '#004000',
                strokeOpacity: 0.8,
                strokeWeight: 3
              }}
              onClick={() => {}}
            />
          ))}
        {dropPinLongLat[0] !== 0 && dropPinLongLat[1] !== 0 ? (
          <Marker
            position={{
              lat: dropPinLongLat[1],
              lng: dropPinLongLat[0]
            }}
            icon={dropPinIcon()}
          />
        ) : (
          ''
        )}
      </GoogleMap>
    </>
  );
};
